// Shared JSend handling for the GPU server budget hooks (docs/api-types/gpu-budgets.md).
//
// - Controllers answer `success` or `error` at HTTP 200; an `error`'s `message` is shown verbatim, and
//   a field error is `message: "Request contains errors"` with a `data.<field>` map.
// - The ROUTER answers `fail` — for every budget route until the backend is deployed, a non-numeric
//   id, or a wrong method (HTTP 405). Any `fail` maps to ONE fixed message: never render `data.msg`
//   (nor a `fail`'s `message`).
// - A non-JSON body is an error too. Never branch on `response.ok`.
import type { GpuJsendEnvelope } from './gpuServerResponse';
import { jsendFieldErrors } from './gpuServerResponse';

/** Shown for the router's `fail` (e.g. the frontend deployed before the backend). */
export const GPU_BUDGETS_UNAVAILABLE_MESSAGE = 'GPU server budgets are not available at the moment.';

/** Any route, `GET` included: an unknown id, another organisation's budget, a deleted one. */
export const BUDGET_NOT_FOUND_MESSAGE = 'Budget not found';

/** A line route: the line is not (or no longer) in this budget. */
export const BUDGET_LINE_NOT_FOUND_MESSAGE = 'Budget line not found';

/**
 * The backend's messages after which a write MAY have committed (a failure after the commit, or a read
 * failing right after it): refetch, and never resubmit automatically.
 */
const MAYBE_SAVED_MESSAGES: readonly string[] = [
  'Could not save the budget, please retry',
  'Budgets are temporarily unavailable, please retry later',
];

/** The caps (the number comes from the backend: match the sentence, not the figure). */
const LIMIT_MESSAGE_RES: readonly RegExp[] = [
  /^An organisation can keep at most \d+ budgets$/,
  /^A budget can hold at most \d+ lines$/,
];

/** Shown when a write's outcome could not be read (a network error, a non-JSON body). */
export const GPU_BUDGET_UNCONFIRMED_MESSAGE = 'The change could not be confirmed.';

/** Appended to the notification of a write whose outcome is unknown. */
export const GPU_BUDGET_MAYBE_SAVED_HINT = 'Check the budget before trying again — the change may have been saved.';

/**
 * Why a budget write failed — decides where the error goes and what to refetch:
 * - `invalid`: field errors (`data.<field>`); nothing was written.
 * - `limit`: a cap (50 budgets / 50 lines); nothing was written, but the list/budget is stale.
 * - `unconfirmed`: the write may have committed (a save/read failure message, a network error, a
 *   non-JSON body, a success missing its payload) — refetch, never resubmit.
 * - `budget_not_found` / `line_not_found`: a colleague deleted it.
 * - `unavailable`: the router's `fail` — nothing ran.
 * - `other`: any other refusal, shown verbatim.
 */
export type GpuBudgetErrorReason =
  | 'invalid'
  | 'limit'
  | 'unconfirmed'
  | 'budget_not_found'
  | 'line_not_found'
  | 'unavailable'
  | 'other';

export interface GpuBudgetWriteError {
  status: 'error';
  message: string;
  /** `data.<field>` of a field error (`name`, `product_id`, `quantity`, `hours_per_month`, `body`, …). */
  fieldErrors: Record<string, string>;
  reason: GpuBudgetErrorReason;
}

/** The message to show for a failed READ: the fixed text for any `fail`, an `error`'s message, else `fallback`. */
export function budgetReadErrorMessage(body: GpuJsendEnvelope | null, fallback: string): string {
  if (body?.status === 'fail') {
    return GPU_BUDGETS_UNAVAILABLE_MESSAGE;
  }
  if (body?.status === 'error' && typeof body.message === 'string' && body.message !== '') {
    return body.message;
  }
  return fallback;
}

/** `true` when a body is the controller's "Budget not found". */
export function isBudgetNotFound(body: GpuJsendEnvelope | null): boolean {
  return body?.status === 'error' && body.message === BUDGET_NOT_FOUND_MESSAGE;
}

const reasonFor = (message: string, fieldErrors: Record<string, string>): GpuBudgetErrorReason => {
  if (message === BUDGET_NOT_FOUND_MESSAGE) {
    return 'budget_not_found';
  }
  if (message === BUDGET_LINE_NOT_FOUND_MESSAGE) {
    return 'line_not_found';
  }
  if (MAYBE_SAVED_MESSAGES.includes(message)) {
    return 'unconfirmed';
  }
  if (LIMIT_MESSAGE_RES.some((re) => re.test(message))) {
    return 'limit';
  }
  return Object.keys(fieldErrors).length > 0 ? 'invalid' : 'other';
};

/**
 * Classify a WRITE's non-success body. A `fail` is the fixed "not available" message; an `error` keeps
 * its verbatim message and field map; anything else (`null` = non-JSON, a success missing its payload)
 * is `unconfirmed`, since the write may have gone through.
 */
export function budgetWriteError(body: GpuJsendEnvelope | null, fallback: string): GpuBudgetWriteError {
  if (body?.status === 'fail') {
    return { status: 'error', message: GPU_BUDGETS_UNAVAILABLE_MESSAGE, fieldErrors: {}, reason: 'unavailable' };
  }
  if (body?.status === 'error') {
    const message = typeof body.message === 'string' && body.message !== '' ? body.message : fallback;
    const fieldErrors = jsendFieldErrors(body);
    return { status: 'error', message, fieldErrors, reason: reasonFor(message, fieldErrors) };
  }
  return { status: 'error', message: GPU_BUDGET_UNCONFIRMED_MESSAGE, fieldErrors: {}, reason: 'unconfirmed' };
}

/** A write whose request threw (network error): it may have reached the backend. */
export const budgetWriteThrown = (): GpuBudgetWriteError => ({
  status: 'error',
  message: GPU_BUDGET_UNCONFIRMED_MESSAGE,
  fieldErrors: {},
  reason: 'unconfirmed',
});
